import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { lovable } from "@/integrations/lovable";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { evaluatePasswordStrength } from "@/lib/password-strength";
import { redeemInviteCode, validateInviteCode } from "@/lib/invite-codes.functions";
import { registerReferralAfterSignup } from "@/lib/referrals.functions";
import { track } from "@/lib/analytics";
import { trackMetaEvent } from "@/lib/metaPixel";
import { getReferralCode, clearReferralCode, getOrCreateDeviceId } from "@/lib/referral";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { seoHead } from "@/lib/seo";

const INVITE_CODE_STORAGE_KEY = "pending_invite_code";

async function tryRegisterReferral() {
  const code = getReferralCode();
  if (!code) return;
  try {
    const result = await registerReferralAfterSignup({
      data: { code, deviceId: getOrCreateDeviceId() || null },
    });
    if (result.ok) {
      track("referral_signup_registered", { referrer_id: result.referrerId });
    }
  } catch (e) {
    console.warn("[signup] referral registration failed:", e);
  } finally {
    clearReferralCode();
  }
}

export const Route = createFileRoute("/signup")({
  head: () =>
    seoHead({
      title: "新規登録 | KAGEM",
      description:
        "KAGEMのアカウントを作成して、AIによる変身動画・画像生成を始めましょう。新規登録で30クレジットプレゼント。",
      path: "/signup",
    }),
  component: SignupPage,
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SignupPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const strength = useMemo(() => evaluatePasswordStrength(password), [password]);
  const allConsents = agreeTerms && agreeAge;
  const canSubmit =
    allConsents && !submitting && email.length > 0 && password.length > 0;

  const handleGoogle = async () => {
    const code = inviteCode.trim();
    if (code) {
      // Pre-validate the code (without consuming) before redirecting to OAuth.
      const validation = await validateInviteCode({ data: { code } });
      if (!validation.ok) {
        setInviteError(validation.message);
        toast.error(validation.message);
        setGoogleLoading(false);
        return;
      }
      // Stash code so we can consume it after OAuth callback completes.
      sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, code);
    }
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
        toast.error("Googleログインに失敗しました。もう一度お試しください。");
        setGoogleLoading(false);
        return;
      }
      // If redirected, browser will navigate away. Otherwise tokens are set.
      if (!result.redirected) {
        if (code) {
          // Consume the code now since we are not redirecting.
          const redeem = await redeemInviteCode({ data: { code } });
          sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
          if (!redeem.ok) {
            toast.error(redeem.message);
            setGoogleLoading(false);
            return;
          }
        }
        track("sign_up", { method: "google" });
        trackMetaEvent("CompleteRegistration", { method: "google" });
        await tryRegisterReferral();
        toast.success("ログインしました");
        navigate({ to: "/" });
      }
    } catch {
      sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
      toast.error("Googleログイン中にエラーが発生しました");
      setGoogleLoading(false);
    }
  };

  const handleApple = async () => {
    const code = inviteCode.trim();
    if (code) {
      const validation = await validateInviteCode({ data: { code } });
      if (!validation.ok) {
        setInviteError(validation.message);
        toast.error(validation.message);
        setAppleLoading(false);
        return;
      }
      sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, code);
    }
    setAppleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
        toast.error("Apple IDログインに失敗しました。もう一度お試しください。");
        setAppleLoading(false);
        return;
      }
      if (!result.redirected) {
        if (code) {
          const redeem = await redeemInviteCode({ data: { code } });
          sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
          if (!redeem.ok) {
            toast.error(redeem.message);
            setAppleLoading(false);
            return;
          }
        }
        track("sign_up", { method: "apple" });
        trackMetaEvent("CompleteRegistration", { method: "apple" });
        await tryRegisterReferral();
        toast.success("ログインしました");
        navigate({ to: "/" });
      }
    } catch {
      sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
      toast.error("Apple IDログイン中にエラーが発生しました");
      setAppleLoading(false);
    }
  };

  const validate = () => {
    let ok = true;
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError("メールアドレスの形式が正しくありません");
      ok = false;
    } else {
      setEmailError(null);
    }
    if (password.length < 8) {
      setPasswordError("パスワードは8文字以上で入力してください");
      ok = false;
    } else if (strength.level === "weak") {
      setPasswordError("パスワードが弱すぎます。英大文字・小文字・数字・記号を組み合わせてください");
      ok = false;
    } else {
      setPasswordError(null);
    }
    return ok;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allConsents) return;
    if (!validate()) return;

    const code = inviteCode.trim();

    setSubmitting(true);
    try {
      // If an invite code is provided, pre-validate it BEFORE creating the auth user.
      if (code) {
        const validation = await validateInviteCode({ data: { code } });
        if (!validation.ok) {
          setInviteError(validation.message);
          toast.error(validation.message);
          setSubmitting(false);
          return;
        }
      }

      // Rate-limit by IP (anti-abuse) BEFORE creating the auth user.
      // TODO (post-launch): If abuse is observed, add SMS phone verification
      // (originally specified in step 6.4) on top of this IP-based limiter.
      try {
        const recaptchaToken = await getRecaptchaToken("signup");
        const { data: rl, error: rlError } = await supabase.functions.invoke(
          "signup-rate-limit",
          { body: { email: email.trim(), recaptcha_token: recaptchaToken } },
        );
        if (rlError) {
          // Edge Function returned non-2xx (e.g. 429 / 403)
          const status = (rlError as { context?: { status?: number } }).context?.status;
          if (status === 429) {
            toast.error(
              "同一の接続元からの登録試行が多すぎます。しばらく時間を置いてから再度お試しください",
            );
            setSubmitting(false);
            return;
          }
          if (status === 403) {
            toast.error("登録内容の検証に失敗しました。ページを再読み込みして再度お試しください。");
            setSubmitting(false);
            return;
          }
          // Other errors: fail open and continue
          console.warn("rate-limit check failed, continuing:", rlError);
        } else if (rl && rl.ok === false) {
          if (rl.error === "recaptcha_failed") {
            toast.error("登録内容の検証に失敗しました。ページを再読み込みして再度お試しください。");
          } else {
            toast.error(
              "同一の接続元からの登録試行が多すぎます。しばらく時間を置いてから再度お試しください",
            );
          }
          setSubmitting(false);
          return;
        }
      } catch (e) {
        // Network error on rate-limit call: fail open
        console.warn("rate-limit invoke error, continuing:", e);
      }

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        const msg = error.message?.toLowerCase() ?? "";
        if (msg.includes("already") || msg.includes("registered") || msg.includes("exists")) {
          toast.error("このメールアドレスは既に登録されています");
        } else if (msg.includes("password")) {
          toast.error("パスワードが要件を満たしていません");
        } else if (msg.includes("network") || msg.includes("fetch")) {
          toast.error("ネットワークエラーが発生しました。接続をご確認ください");
        } else {
          toast.error(`登録に失敗しました: ${error.message}`);
        }
        setSubmitting(false);
        return;
      }

      // Edge case: identities array empty → email already in use (no error returned)
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        toast.error("このメールアドレスは既に登録されています");
        setSubmitting(false);
        return;
      }

      // Signup succeeded → consume the code now if provided.
      if (code) {
        const redeem = await redeemInviteCode({ data: { code } });
        if (!redeem.ok) {
          // Race condition: someone else used it. The auth user still exists but won't be able to use the app.
          toast.error(redeem.message);
          setSubmitting(false);
          return;
        }
      }

      track("sign_up", { method: "email" });
      trackMetaEvent("CompleteRegistration", { method: "email" });
      // Note: registerReferralAfterSignup requires an authenticated session.
      // For email signup with confirmation, the session is created on confirm.
      // We attempt now (works if auto-confirm enabled), and again on root after confirm.
      await tryRegisterReferral();
      toast.success("確認メールを送信しました");
      navigate({ to: "/signup/confirm", search: { email: email.trim() } as never });
    } catch {
      toast.error("ネットワークエラーが発生しました。接続をご確認ください");
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gradient-hero relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-10 sm:px-6">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Link to="/">
            <BrandLogo size="md" />
          </Link>
        </div>

        <Card className="border-border/60 shadow-2xl shadow-brand-primary/10 backdrop-blur-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">新規登録</CardTitle>
            <CardDescription>アカウントを作成して、変身動画を作り始めましょう</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Invite code input hidden — open signup, code is optional */}
            {/*
            <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <Label htmlFor="invite-code" className="text-sm font-semibold">
                招待コード <span className="text-destructive">*</span>
              </Label>
              <Input
                id="invite-code"
                type="text"
                autoComplete="off"
                placeholder="招待コードを入力"
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value);
                  if (inviteError) setInviteError(null);
                }}
                aria-invalid={!!inviteError}
                className="bg-background"
              />
              {inviteError ? (
                <p className="text-xs text-destructive">{inviteError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  現在は招待コードをお持ちの方のみ登録いただけます
                </p>
              )}
            </div>
            */}

            {/* Google */}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="relative h-11 w-full border-2 font-medium"
                onClick={handleGoogle}
                disabled={googleLoading || appleLoading || submitting}
              >
                {googleLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span>Googleで登録</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="relative h-11 w-full border-2 bg-black font-medium text-white hover:bg-black/90 hover:text-white dark:bg-white dark:text-black dark:hover:bg-white/90 dark:hover:text-black"
                onClick={handleApple}
                disabled={googleLoading || appleLoading || submitting}
              >
                {appleLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <AppleIcon />
                )}
                <span>Apple IDで登録</span>
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">または</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  aria-invalid={!!emailError}
                  required
                />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">パスワード</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="8文字以上"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError(null);
                    }}
                    aria-invalid={!!passwordError}
                    minLength={8}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "パスワードを非表示" : "パスワードを表示"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Strength meter */}
                {password.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={cn(
                            "h-1.5 flex-1 rounded-full transition-colors",
                            i <= strength.score
                              ? strength.level === "weak"
                                ? "bg-destructive"
                                : strength.level === "medium"
                                  ? "bg-amber-500"
                                  : "bg-emerald-500"
                              : "bg-muted",
                          )}
                        />
                      ))}
                    </div>
                    <p
                      className={cn(
                        "text-xs",
                        strength.level === "weak" && "text-destructive",
                        strength.level === "medium" && "text-amber-600 dark:text-amber-500",
                        strength.level === "strong" && "text-emerald-600 dark:text-emerald-500",
                      )}
                    >
                      パスワード強度: {strength.label}
                    </p>
                  </div>
                )}
                {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
              </div>

              {/* Consents */}
              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                <ConsentRow
                  id="agree-terms"
                  checked={agreeTerms}
                  onChange={setAgreeTerms}
                  label={
                    <>
                      <a href="/terms" target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">利用規約</a>
                      {" と "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">プライバシーポリシー</a>
                      に同意します
                    </>
                  }
                />
                <ConsentRow
                  id="agree-age"
                  checked={agreeAge}
                  onChange={setAgreeAge}
                  label={<>私は18歳以上です</>}
                />
              </div>

              <Button
                type="submit"
                className="h-11 w-full bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 hover:opacity-95"
                disabled={!canSubmit}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                登録する
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                既にアカウントをお持ちの方は{" "}
                <a href="/login" className="text-primary underline-offset-4 hover:underline">
                  ログイン
                </a>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function ConsentRow({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
        required
      />
      <Label htmlFor={id} className="cursor-pointer text-xs leading-relaxed font-normal">
        {label}
      </Label>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z"/>
    </svg>
  );
}
